import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { listWorkflows, getWorkflow } from "@/lib/orchestrator.functions";

export const Route = createFileRoute("/_authenticated/app/workflows")({
  component: WorkflowsPage,
  errorComponent: ({ error }) => <div className="p-6 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-6 text-sm">Nicht gefunden.</div>,
});

function WorkflowsPage() {
  const list = useServerFn(listWorkflows);
  const get = useServerFn(getWorkflow);
  const [openId, setOpenId] = useState<string | null>(null);

  const wfs = useQuery({ queryKey: ["wfs"], queryFn: () => list({ data: {} }), refetchInterval: 4000 });
  const detail = useQuery({
    queryKey: ["wf", openId],
    queryFn: () => get({ data: { workflow_id: openId! } }),
    enabled: !!openId,
    refetchInterval: openId ? 2000 : false,
  });

  return (
    <PortalShell nav={[{ to: "/app/portal", label: "Übersicht" }, { to: "/app/angelika", label: "Angelika" }]}>
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Agenten-Workflows</h1>

        <Card>
          <CardHeader><CardTitle>Letzte Workflows</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {(wfs.data?.workflows ?? []).map((w: any) => (
              <div key={w.id} className="flex items-center justify-between rounded border p-2">
                <div>
                  <div className="font-medium">{w.workflow_name}</div>
                  <div className="text-xs text-muted-foreground">
                    <Badge variant="outline" className="mr-1">{w.status}</Badge>
                    {w.current_step ?? "—"} · {new Date(w.created_at).toLocaleString()}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setOpenId(w.id)}>Details</Button>
              </div>
            ))}
            {(wfs.data?.workflows ?? []).length === 0 && <p className="text-muted-foreground">Keine Workflows.</p>}
          </CardContent>
        </Card>

        {openId && (
          <Card>
            <CardHeader><CardTitle>Workflow {openId.slice(0, 8)}…</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div>
                <div className="font-medium mb-1">Schritte</div>
                {(detail.data?.steps ?? []).map((s: any) => (
                  <div key={s.id} className="rounded border p-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{s.step_no}</Badge>
                      <span className="font-medium">{s.agent_name}</span>
                      <Badge variant="secondary">{s.status}</Badge>
                      {s.duration_ms != null && <span className="text-muted-foreground">{s.duration_ms} ms</span>}
                    </div>
                    {s.error && <div className="text-destructive">{s.error}</div>}
                  </div>
                ))}
              </div>
              <div>
                <div className="font-medium mb-1">Agentenkommunikation</div>
                {(detail.data?.messages ?? []).map((m: any) => (
                  <div key={m.id} className="rounded border p-2 mb-1">
                    {m.from_agent} → {m.to_agent} ({m.message_type})
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PortalShell>
  );
}
